import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { obterDataCadastro, obterIdTenent } from '../../services/seguranca';
import { parseDataBRParaDate } from '../../services/utilitarios';
import api from '../../services/api';
import { rastrearTeste } from '../../services/rastreamentoTeste';
import {
  FaRocket,
  FaFileContract,
  FaCar,
  FaUserTie,
  FaHandshake,
  FaMoneyBill,
  FaFileInvoiceDollar,
  FaTools,
  FaCheck,
  FaTrophy,
  FaLock,
  FaTimes,
} from 'react-icons/fa';
import styles from './OnboardingChecklist.module.css';

const STORAGE_KEY = 'locasmart_onboarding_dismissed';

/** Abre o "Primeiros Passos" de qualquer tela (ex.: "Explorar o Sistema" no fim do onboarding). */
export const EVENTO_ABRIR_PRIMEIROS_PASSOS = 'locasmart:abrir-primeiros-passos';
export const abrirPrimeirosPassos = (origem: OrigemPrimeirosPassos = 'explorar') =>
  window.dispatchEvent(new CustomEvent<OrigemPrimeirosPassos>(EVENTO_ABRIR_PRIMEIROS_PASSOS, { detail: origem }));

/** Quem abriu o "Primeiros Passos": sozinho no painel, o botão do topo ou o "Explorar o Sistema". */
export type OrigemPrimeirosPassos = 'automatico' | 'botao' | 'explorar';

interface OnboardingStep {
  key: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  route: string;
  dependeDe?: string[];
}

const STEPS: OnboardingStep[] = [
  {
    key: 'modelosContrato',
    title: 'Criar Modelo de Contrato',
    description: 'Configure o modelo que será usado nas suas locações',
    icon: <FaFileContract />,
    route: '/modelo-contrato',
  },
  {
    key: 'veiculos',
    title: 'Cadastrar Veículo',
    description: 'Adicione seu primeiro veículo à frota',
    icon: <FaCar />,
    route: '/veiculo',
  },
  {
    key: 'locatarios',
    title: 'Cadastrar Locatário',
    description: 'Registre os dados do seu primeiro cliente',
    icon: <FaUserTie />,
    route: '/locatarios',
  },
  {
    key: 'locacoes',
    title: 'Criar Locação',
    description: 'Faça sua primeira locação vinculando veículo e locatário',
    icon: <FaHandshake />,
    route: '/locacao?criar=true',
    dependeDe: ['veiculos', 'locatarios'],
  },
  {
    key: 'pagamentosBaixados',
    title: 'Dar Baixa em Pagamento',
    description: 'Registre o recebimento de um pagamento',
    icon: <FaMoneyBill />,
    route: '/pagamento?Pendentes=true',
    dependeDe: ['locacoes'],
  },
  {
    key: 'lancamentos',
    title: 'Criar Lançamento',
    description: 'Registre uma receita ou despesa avulsa',
    icon: <FaFileInvoiceDollar />,
    route: '/lancamento',
  },
  {
    key: 'manutencoes',
    title: 'Registrar Manutenção',
    description: 'Cadastre um serviço de manutenção em um veículo',
    icon: <FaTools />,
    route: '/manutencoes',
  },
];

interface OnboardingChecklistProps {
  active?: boolean;
  /** De onde veio a abertura por `active` (só para a telemetria do período de teste). */
  origem?: OrigemPrimeirosPassos;
  onFinish?: () => void;
}

interface ProgressData {
  modelosContrato: number;
  veiculos: number;
  locatarios: number;
  locacoes: number;
  pagamentosBaixados: number;
  lancamentos: number;
  manutencoes: number;
  [key: string]: number;
}

export function OnboardingChecklist({ active, origem, onFinish }: OnboardingChecklistProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState<ProgressData | null>(null);
  const [recentlyCompleted, setRecentlyCompleted] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const prevProgressRef = useRef<ProgressData | null>(null);
  const token = useAppSelector(state => state.token);
  const history = useHistory();

  const fetchProgress = async () => {
    const idEmpresa = obterIdTenent(token);
    if (!idEmpresa) return;

    setLoading(true);
    try {
      const response = await api.get(`OnboardingProgress/${idEmpresa}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newProgress = response.data as ProgressData;

      // Detectar steps recém-concluídos
      if (prevProgressRef.current) {
        const newlyDone = STEPS
          .filter(s => {
            const wasDone = (prevProgressRef.current![s.key] ?? 0) > 0;
            const isDone = (newProgress[s.key] ?? 0) > 0;
            return !wasDone && isDone;
          })
          .map(s => s.key);
        if (newlyDone.length > 0) {
          setRecentlyCompleted(newlyDone);
          setTimeout(() => setRecentlyCompleted([]), 3000);
        }
      }

      prevProgressRef.current = newProgress;
      setProgress(newProgress);
    } catch (error) {
      console.error('Erro ao buscar progresso do onboarding', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (active) {
      rastrearTeste('primeiros_passos_aberto', { origem: origem ?? 'botao' });
      setIsVisible(true);
      fetchProgress();
      return;
    }

    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) return;

    if (token) {
      try {
        const dataCadastroStr = obterDataCadastro(token);
        if (dataCadastroStr) {
          const dataCadastro = parseDataBRParaDate(dataCadastroStr);
          if (dataCadastro) {
            const hoje = new Date();
            const diffTime = Math.abs(hoje.getTime() - dataCadastro.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays <= 30) {
              const timer = setTimeout(() => {
                rastrearTeste('primeiros_passos_aberto', { origem: 'automatico' });
                setIsVisible(true);
                fetchProgress();
              }, 1000);
              return () => clearTimeout(timer);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao verificar data de cadastro para onboarding', error);
      }
    }
  }, [active, token]);

  const handleDismiss = (como: 'x' | 'depois') => {
    rastrearTeste('primeiros_passos_fechado', { como });
    setIsVisible(false);
    localStorage.setItem(STORAGE_KEY, 'true');
    onFinish?.();
  };

  const handleGoToStep = (step: OnboardingStep) => {
    rastrearTeste('primeiros_passos_passo_clicado', { passo: step.key });
    setIsVisible(false);
    onFinish?.();
    localStorage.setItem('locasmart_active_guide', step.key);
    history.push(step.route);
  };

  if (!isVisible) return null;

  const completedCount = progress
    ? STEPS.filter(s => (progress[s.key] ?? 0) > 0).length
    : 0;
  const totalSteps = STEPS.length;
  const allDone = completedCount === totalSteps && progress !== null;
  const percent = progress ? Math.round((completedCount / totalSteps) * 100) : 0;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {allDone ? (
          <div className={styles.congratsContainer}>
            <div className={styles.congratsIcon}>
              <FaTrophy color="#FFD700" />
            </div>
            <h2>Parabéns!</h2>
            <p>
              Você completou todos os primeiros passos do LocaSmart Pro.
              <br />
              Seu sistema está pronto para uso!
            </p>
            <div className={styles.congratsButtons}>
              <button
                className={styles.btnTreinamento}
                onClick={() => window.open('https://treinamento.locasmartpro.com.br/', '_blank')}
              >
                Assistir treinamento completo
              </button>
              <button className={styles.btnFechar} onClick={() => handleDismiss('depois')}>
                Começar a usar
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.header}>
              <div className={styles.headerIcon}>
                <FaRocket />
              </div>
              <div className={styles.headerContent}>
                <h2>Primeiros Passos</h2>
                <p>Complete as etapas abaixo para configurar seu sistema</p>
              </div>
              <button type="button" className={styles.btnFecharTopo} onClick={() => handleDismiss('x')} aria-label="Fechar" title="Fechar">
                <FaTimes />
              </button>
            </div>

            <div className={styles.progressSection}>
              <div className={styles.progressInfo}>
                <span>{completedCount} de {totalSteps} etapas concluídas</span>
                <span className={styles.progressPercent}>{percent}%</span>
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>

            <ul className={styles.stepsList}>
              {STEPS.map(step => {
                const done = progress ? (progress[step.key] ?? 0) > 0 : false;
                const isRecent = recentlyCompleted.includes(step.key);
                const dependenciasPendentes = step.dependeDe && progress
                  ? step.dependeDe.filter(dep => (progress[dep] ?? 0) === 0)
                  : [];
                const bloqueado = dependenciasPendentes.length > 0;
                return (
                  <li key={step.key} className={`${styles.stepItem} ${bloqueado ? styles.stepItemBloqueado : ''} ${isRecent ? styles.stepItemRecent : ''}`}>
                    <div
                      className={`${styles.stepCheckbox} ${done ? styles.stepCheckboxDone : ''}`}
                    >
                      {done && <FaCheck size={12} />}
                    </div>
                    <div className={`${styles.stepIcon} ${done ? styles.stepIconDone : ''}`}>
                      {step.icon}
                    </div>
                    <div className={styles.stepContent}>
                      <div className={`${styles.stepTitle} ${done ? styles.stepTitleDone : ''}`}>
                        {step.title}
                      </div>
                      <div className={styles.stepDesc}>
                        {bloqueado
                          ? `Primeiro complete: ${dependenciasPendentes.map(dep => STEPS.find(s => s.key === dep)?.title).join(' e ')}`
                          : step.description
                        }
                      </div>
                    </div>
                    <div className={styles.stepAction}>
                      {done ? (
                        <span className={styles.labelConcluido}>Concluído</span>
                      ) : bloqueado ? (
                        <FaLock size={14} color="#bbb" />
                      ) : (
                        <button
                          className={styles.btnFazerAgora}
                          onClick={() => handleGoToStep(step)}
                        >
                          Fazer agora
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className={styles.footer}>
              <button className={styles.btnDepois} onClick={() => handleDismiss('depois')}>
                Fazer depois
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
